import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "scopeMode",
    "templateSection",
    "templateQuery",
    "templateSelect",
    "templateId",
    "templateSelection",
    "templateResults",
    "registrySection",
    "registryExport",
    "registryColumnsSection",
    "fieldGroups",
    "fieldsSection",
    "previewSection",
    "previewBody",
    "recentExports",
    "status",
    "indexFiltersSection",
    "indexFiltersBar",
    "indexFiltersMasonry",
    "indexFilters",
    "indexFiltersTrack",
    "indexFilterItem",
    "indexFilterDot",
    "runCsvButton",
    "runXlsxButton"
  ]

  static values = {
    previewUrl: String,
    runUrl: String,
    templatesSearchUrl: String,
    streamStatusUrl: String,
    indexOptions: Object,
    indexQuery: String,
    searchFrame: String,
    defaultScopeMode: String,
    hubVariant: String,
    forcedProductScope: Boolean,
    openOnConnect: Boolean,
    companyTemplates: Array,
    presetTemplate: Object,
    loading: String,
    previewFailed: String,
    fieldsHint: String,
    selectDistributorHint: String,
    selectTemplateHint: String,
    selectAllFields: String,
    deselectAllFields: String,
    previewPlaceholder: String,
    registryColumnsLabel: String,
    rowCountContracts: String,
    rowCountLines: String,
    completedTitle: String,
    errorTitle: String,
    preparingTitle: String,
    preparingDescription: String,
    downloadFile: String,
    templateSearchMinLength: String,
    templateSearchEmpty: String,
    templateSearchError: String
  }

  connect() {
    this.previewData = null
    this.previewRequestId = 0
    this.registryExportsData = []
    this.streamPollInterval = null
    this.activeStreamId = null
    this.streamFinished = false

    this.modalElement = this.element.closest(".modal")
    this.listQuery = (this.indexQueryValue || "").trim()
    this.previewOnShow = false
    this.indexFiltersDirty = false
    this.modalShown = this.modalShown.bind(this)
    this.boundIndexFiltersChanged = (event) => this.indexFiltersChanged(event)
    this.modalElement?.addEventListener("show.bs.modal", this.modalShown)
    document.addEventListener("index-options:changed", this.boundIndexFiltersChanged)
    this.smoothProgressStream = this.smoothProgressStream.bind(this)
    document.addEventListener("turbo:before-stream-render", this.smoothProgressStream)
    this.scheduleOpen()

    if (this.forcedProductScopeValue || this.hubVariantValue === "template") {
      this.initializeTemplateSelect(this.presetTemplateValue)
      this.syncXlsxButtonVisibility()
      if (this.canPreview()) this.previewWhenShown()
      this.resumePendingStream()
      return
    }

    if (this.hasTemplateSelectTarget) {
      this.initializeTemplateSelect(this.presetTemplateValue)
      this.syncXlsxButtonVisibility()
    }

    if (this.hasDefaultScopeModeValue) {
      this.setScopeMode(this.defaultScopeModeValue)
    }

    this.scopeModeChanged({ skipPreview: true })

    if (this.canPreview()) {
      this.previewWhenShown()
    }
  }

  disconnect() {
    clearTimeout(this.openTimer)
    this.stopStreamPolling()
    this.modalElement?.removeEventListener("show.bs.modal", this.modalShown)
    document.removeEventListener("index-options:changed", this.boundIndexFiltersChanged)
    document.removeEventListener("turbo:before-stream-render", this.smoothProgressStream)
  }

  // Rendue à la demande par la fiche du catalogue (une frame, à chaque clic : Insurance::ProductExportsController), la
  // modale s'ouvre d'elle-même, par le chemin d'un bouton : « show.bs.modal » lance alors l'aperçu. Différé d'un tick :
  // le contrôleur modal la déplace dans #modals dès son branchement, ce qui débranche puis rebranche celui-ci ; seule
  // l'ouverture du dernier branchement compte (le temps est annulé au débranchement).
  scheduleOpen() {
    clearTimeout(this.openTimer)
    if (!this.openOnConnectValue || !this.modalElement) return

    this.openTimer = setTimeout(() => {
      if (!this.element.isConnected || this.modalElement.classList.contains("show")) return

      Modal.getOrCreateInstance(this.modalElement).show()
    }, 0)
  }

  // Rendue avec chaque index, la modale reste le plus souvent fermée : son premier aperçu
  // attend qu'on l'ouvre (son déplacement dans #modals reconnecte d'ailleurs ce contrôleur).
  previewWhenShown() {
    if (this.modalElement && !this.modalElement.classList.contains("show")) {
      this.previewOnShow = true
      return
    }

    this.preview()
  }

  modalShown() {
    // `indexFiltersDirty` : la liste a changé de filtres pendant que la modale était fermée.
    // Les options sont déjà à jour (événement), mais les filtres affichés ne le sont pas.
    const snapshotChanged = this.syncLiveIndexState() || this.indexFiltersDirty
    this.indexFiltersDirty = false
    if (!this.previewOnShow && !snapshotChanged) return

    this.previewOnShow = false
    if (this.canPreview()) {
      this.preview()
      return
    }

    // Par produit sans produit encore choisi : l'aperçu attend, mais les pastilles
    // doivent déjà coller à la liste (statut, dates, recherche…).
    this.refreshIndexFilters()
  }

  // La recherche / les filtres de l'index ne rechargent que la frame de résultats, pas
  // l'en-tête qui a rendu cette modale. À l'ouverture, on reprend l'état affiché.
  syncLiveIndexState() {
    const queryChanged = this.syncIndexQuery()
    const filtersChanged = this.syncIndexFiltersFromLiveList()
    return queryChanged || filtersChanged
  }

  syncIndexQuery() {
    if (!/^[\w-]+$/.test(this.searchFrameValue || "")) return false

    const input =
      this.searchResultsRoot().closest?.("[data-controller~='search']")?.querySelector('input[name="query"]') ||
      document.querySelector(`form[data-turbo-frame="${this.searchFrameValue}"] input[name="query"]`)
    if (!input) return false

    const query = input.value.trim()
    if (query === this.listQuery) return false
    this.listQuery = query

    const options = this.currentIndexOptions()
    if (query) {
      options.query = query
    } else {
      delete options.query
    }
    this.indexOptionsValue = options
    this.indexQueryValue = query
    return true
  }

  syncIndexFiltersFromLiveList() {
    const live = this.readLiveIndexOptions()
    if (!live) return false

    const current = this.currentIndexOptions()
    const next = { ...current }
    let changed = false

    if (JSON.stringify(live.f || {}) !== JSON.stringify(current.f || {})) {
      if (live.f && Object.keys(live.f).length > 0) {
        next.f = live.f
      } else {
        delete next.f
      }
      changed = true
    }

    if ((live.sort_by || "") !== (current.sort_by || "") || String(live.sort_reverse) !== String(current.sort_reverse)) {
      if (live.sort_by) {
        next.sort_by = live.sort_by
        next.sort_reverse = live.sort_reverse
      } else {
        delete next.sort_by
        delete next.sort_reverse
      }
      changed = true
    }

    if (!changed) return false

    this.indexOptionsValue = next
    return true
  }

  currentIndexOptions() {
    const raw = this.indexOptionsValue
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw)
      } catch (_error) {
        return {}
      }
    }

    return structuredClone(raw || {})
  }

  readLiveIndexOptions() {
    const root = this.searchResultsRoot()
    const fromDataset = (element, key) => {
      const raw = element?.dataset?.[key]
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch (_error) {
        return null
      }
    }

    const fromActiveFilters = fromDataset(
      root.querySelector("[data-index-active-filters-options-value]"),
      "indexActiveFiltersOptionsValue"
    )
    if (fromActiveFilters) return fromActiveFilters

    const fromHeaderFilter = fromDataset(
      root.querySelector("[data-index-header-filter-options-value]"),
      "indexHeaderFilterOptionsValue"
    )
    if (fromHeaderFilter) return fromHeaderFilter

    const raw = new URL(window.location.href).searchParams.get("options")
    if (!raw) return null
    try {
      return JSON.parse(raw)
    } catch (_error) {
      return null
    }
  }

  // Une liste embarquée sans `namespace` (ex. contrats d'une société) a pour frame
  // `search_results`, l'id que porte aussi la recherche globale, placée plus haut dans la
  // page : `getElementById` rendrait celle-ci, sans filtres. On prend celle d'un index.
  searchResultsRoot() {
    if (!/^[\w-]+$/.test(this.searchFrameValue || "")) return document

    const candidates = [...document.querySelectorAll(`[id="${this.searchFrameValue}"]`)]
    return (
      candidates.find((element) => element.closest("[data-controller~='dashboard-index']")) ||
      candidates[0] ||
      document
    )
  }

  async refreshIndexFilters() {
    if (!this.indexFiltersContainer()) return

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify(this.buildPayload())
      })
      if (!response.ok) return

      const payload = await response.json()
      this.renderIndexFilters(payload.index_filters_html)
    } catch (_error) {
      // L'aperçu complet reprendra les filtres dès qu'un produit est choisi.
    }
  }

  // Filtres renvoyés par l'aperçu : masonry (contrats) ou pastilles (sociétés).
  renderIndexFilters(html) {
    const target = this.indexFiltersContainer()
    if (!target || typeof html !== "string") return

    target.innerHTML = html
    this.syncIndexFiltersSectionVisibility()
  }

  indexFiltersContainer() {
    if (this.hasIndexFiltersMasonryTarget) return this.indexFiltersMasonryTarget
    if (this.hasIndexFiltersBarTarget) return this.indexFiltersBarTarget
    return null
  }

  indexFiltersChanged(event) {
    // Plusieurs listes peuvent cohabiter (sections d'une show) : seule la nôtre nous concerne.
    const frameId = event.detail?.frameId
    if (frameId && this.searchFrameValue && frameId !== this.searchFrameValue) return

    const options = event.detail?.options
    if (options) {
      const current = this.currentIndexOptions()
      if (options.f) current.f = options.f
      else delete current.f
      if (options.sort_by) {
        current.sort_by = options.sort_by
        current.sort_reverse = options.sort_reverse
      } else {
        delete current.sort_by
        delete current.sort_reverse
      }
      this.indexOptionsValue = current
    }

    if (!this.modalElement?.classList.contains("show")) {
      this.indexFiltersDirty = true
      return
    }

    if (this.canPreview()) this.preview()
    else this.refreshIndexFilters()
  }

  removeIndexFilter(event) {
    event.preventDefault()
    event.stopPropagation()

    const key = event.params.key
    if (!key) return

    const options = this.currentIndexOptions()
    const f = { ...(options.f || {}) }

    if (key === "query") {
      delete options.query
      this.indexQueryValue = ""
    } else if (key === "sort") {
      delete options.sort_by
      delete options.sort_reverse
    } else if (key.startsWith("f:")) {
      const attr = key.slice(2)
      delete f[attr]
      if (String(options.sort_by) === attr) {
        delete options.sort_by
        delete options.sort_reverse
      }
    }

    if (Object.keys(f).length === 0) {
      delete options.f
    } else {
      options.f = f
    }

    this.indexOptionsValue = options
    this.removeFilterChip(key)
    this.syncIndexFiltersSectionVisibility()

    if (this.canPreview()) this.preview()
  }

  clearIndexFilters(event) {
    event.preventDefault()
    event.stopPropagation()

    const options = this.currentIndexOptions()
    delete options.f
    delete options.query
    delete options.sort_by
    delete options.sort_reverse

    this.indexOptionsValue = options
    this.indexQueryValue = ""

    if (this.hasIndexFiltersTrackTarget) {
      this.indexFiltersTrackTarget.innerHTML = ""
    }
    this.syncIndexFiltersSectionVisibility()

    if (this.canPreview()) this.preview()
  }

  removeFilterChip(key) {
    if (!this.hasIndexFilterItemTarget) return

    const item = this.activeFilterItems().find((el) => el.dataset.filterKey === key)
    if (!item) return

    const previous = item.previousElementSibling
    if (previous?.matches("[data-exports-hub-target~='indexFilterDot']")) {
      this.leave(previous)
    } else {
      const next = item.nextElementSibling
      if (next?.matches("[data-exports-hub-target~='indexFilterDot']")) this.leave(next)
    }

    this.leave(item)
  }

  // Pastilles encore affichées, hors celles qui s'effacent.
  activeFilterItems() {
    return this.indexFilterItemTargets.filter((el) => !el.classList.contains("exports-hub-leaving"))
  }

  // Sortie animée (cf. _exports_hub.scss). `animationend` arrive aussi en mouvement réduit
  // (durée ramenée à 0.01ms) ; le délai de secours couvre un élément déjà masqué.
  leave(element) {
    element.classList.add("exports-hub-leaving")
    const remove = () => element.remove()
    element.addEventListener("animationend", remove, { once: true })
    window.setTimeout(remove, 400)
  }

  syncIndexFiltersSectionVisibility() {
    if (!this.hasIndexFiltersSectionTarget) return

    const hideForRegistry = this.registryExportSelected()
    if (this.hasIndexFiltersMasonryTarget) {
      const empty = this.indexFiltersMasonryTarget.childElementCount === 0
      this.indexFiltersSectionTarget.classList.toggle("d-none", empty || hideForRegistry)
      return
    }

    const hasItems = this.activeFilterItems().length > 0
    this.indexFiltersSectionTarget.classList.toggle("d-none", !hasItems || hideForRegistry)
  }

  scopeModeChanged(event = {}) {
    if (this.forcedProductScopeValue || this.hubVariantValue === "template") return

    const templateMode = this.selectedScopeMode() === "template"

    this.updateScopeTabStyles()

    if (this.hasTemplateSectionTarget) {
      this.templateSectionTarget.classList.toggle("d-none", !templateMode)
    }

    if (!templateMode) {
      this.clearTemplateSelection()
      this.resetRegistryUi()
      this.syncXlsxButtonVisibility()
    }

    if (event.skipPreview) return

    if (this.canPreview()) {
      this.preview()
    } else {
      this.resetPreviewPlaceholder()
      this.renderFieldsPlaceholder(this.templateSelectionHint())
      this.setStatus("")
      this.refreshIndexFilters()
    }
  }

  setScopeMode(mode) {
    this.scopeModeTargets.forEach((input) => {
      input.checked = input.value === mode
    })
    this.updateScopeTabStyles()
  }

  updateScopeTabStyles() {
    this.scopeModeTargets.forEach((input) => {
      const label = this.element.querySelector(`label[for="${input.id}"]`)
      label?.classList.toggle("active", input.checked)
    })
  }

  initializeTemplateSelect(presetTemplate) {
    if (this.hasTemplateSelectTarget && presetTemplate?.id) {
      this.templateSelectTarget.value = String(presetTemplate.id)
    }

    if (presetTemplate?.id) {
      this.applyPresetTemplate(presetTemplate)
    } else {
      this.clearTemplateSelection()
    }
  }

  templateSelectChanged() {
    const template = this.selectedCompanyTemplate()
    if (!template) {
      this.clearTemplateSelection()
      this.resetRegistryUi()
      this.resetPreviewPlaceholder()
      this.renderFieldsPlaceholder(this.templateSelectionHint())
      this.setStatus("")
      return
    }

    this.applyPresetTemplate(template)
    this.syncXlsxButtonVisibility()
    if (this.canPreview()) this.preview()
  }

  selectedCompanyTemplate() {
    if (!this.hasTemplateSelectTarget) return null

    const templateId = this.templateSelectTarget.value
    if (!templateId) return null

    return (this.companyTemplatesValue || []).find((template) => String(template.id) === String(templateId)) || null
  }

  clearTemplateSelection() {
    if (this.hasTemplateIdTarget) this.templateIdTarget.value = ""
    if (this.hasTemplateSelectTarget) this.templateSelectTarget.value = ""
    if (this.hasTemplateQueryTarget) this.templateQueryTarget.value = ""
    if (this.hasTemplateSelectionTarget) {
      this.templateSelectionTarget.textContent = ""
      this.templateSelectionTarget.classList.add("d-none")
    }
    if (this.hasTemplateResultsTarget) this.templateResultsTarget.classList.add("d-none")
  }

  templateSelectionHint() {
    return this.usesTemplateSelect() ? this.selectTemplateHintValue : this.selectDistributorHintValue
  }

  usesTemplateSelect() {
    return this.hasTemplateSelectTarget
  }

  selectedTemplateId() {
    if (this.usesTemplateSelect() && this.hasTemplateSelectTarget) {
      return this.templateSelectTarget.value
    }

    return this.templateIdTarget?.value || ""
  }

  applyPresetTemplate(template) {
    if (this.hasTemplateIdTarget) this.templateIdTarget.value = template.id
    if (this.hasTemplateSelectTarget) this.templateSelectTarget.value = String(template.id)
    if (this.hasTemplateQueryTarget) {
      this.templateQueryTarget.value = ""
    }
    if (this.hasTemplateSelectionTarget) {
      this.templateSelectionTarget.textContent = template.title || ""
      this.templateSelectionTarget.classList.toggle("d-none", !template.title)
    }
    this.updateRegistryExports(template.available_exports || [])
    this.syncXlsxButtonVisibility()
  }

  // Les modales sans export métier (sociétés) n'ont pas de cible registryExport :
  // Stimulus lève sur une cible absente, `?.` ne suffit pas.
  registryExportKey() {
    return this.hasRegistryExportTarget ? this.registryExportTarget.value : ""
  }

  updateRegistryExports(exports) {
    const previousKey = this.registryExportKey()
    this.registryExportsData = exports

    if (!exports.length) {
      if (this.hasRegistrySectionTarget) this.registrySectionTarget.classList.add("d-none")
      this.resetRegistrySelection()
      return
    }

    this.populateRegistryExports(exports)
    this.registrySectionTarget.classList.remove("d-none")

    if (previousKey && exports.some((item) => item.key === previousKey)) {
      this.registryExportTarget.value = previousKey
      this.updateIndexFiltersVisibility()
      const selected = exports.find((item) => item.key === previousKey)
      this.showRegistryColumns(selected?.headers || [])
      this.fieldsSectionTarget.classList.add("d-none")
    } else {
      this.resetRegistrySelection()
    }
  }

  populateRegistryExports(exports) {
    const placeholder = this.registryExportTarget.dataset.placeholder || ""
    this.registryExportTarget.innerHTML = `<option value="">${placeholder}</option>`
    exports.forEach((item) => {
      const option = document.createElement("option")
      option.value = item.key
      option.textContent = item.label
      this.registryExportTarget.appendChild(option)
    })
  }

  registryExportChanged() {
    const exportKey = this.registryExportTarget.value
    this.updateIndexFiltersVisibility()

    if (exportKey) {
      const selected = this.registryExportsData.find((item) => item.key === exportKey)
      this.showRegistryColumns(selected?.headers || [])
      this.fieldsSectionTarget.classList.add("d-none")
    } else {
      this.resetRegistrySelection()
      if (this.canPreview()) this.preview()
      return
    }

    if (this.canPreview()) this.preview()
  }

  updateIndexFiltersVisibility() {
    this.syncIndexFiltersSectionVisibility()
  }

  registryExportSelected() {
    return Boolean(this.registryExportKey())
  }

  showRegistryColumns(headers) {
    const collapseId = "exports_hub_registry_columns"
    const label = this.registryColumnsLabelValue || "Colonnes"
    const items = headers
      .map((header) => `<li class="list-group-item py-1 px-2">${this.escapeHtml(header)}</li>`)
      .join("")

    this.registryColumnsSectionTarget.innerHTML = `
      <div class="border rounded">
        <div class="d-flex align-items-center px-2 py-2 pointer"
             onclick="this.querySelector('.collapse-arrow').click()">
          <i class="fas fa-angle-right opacity-50 collapse-arrow collapsed me-2"
             role="button"
             data-bs-toggle="collapse"
             data-bs-target="#${collapseId}"
             aria-expanded="false"
             aria-controls="${collapseId}"></i>
          <span class="small fw-semibold flex-grow-1">${this.escapeHtml(label)}</span>
          <span class="badge text-bg-secondary">${headers.length}</span>
        </div>
        <div id="${collapseId}" class="collapse">
          <ul class="list-group list-group-flush small mb-0 border-top">${items}</ul>
        </div>
      </div>
    `
    this.registryColumnsSectionTarget.classList.remove("d-none")
  }

  escapeHtml(text) {
    return String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  resetRegistrySelection() {
    if (this.hasRegistryExportTarget) this.registryExportTarget.value = ""
    if (this.hasRegistryColumnsSectionTarget) {
      this.registryColumnsSectionTarget.classList.add("d-none")
      this.registryColumnsSectionTarget.innerHTML = ""
    }
    if (this.hasFieldsSectionTarget) this.fieldsSectionTarget.classList.remove("d-none")
    this.updateIndexFiltersVisibility()
  }

  resetRegistryUi() {
    this.registryExportsData = []
    this.resetRegistrySelection()
    if (this.hasRegistrySectionTarget) this.registrySectionTarget.classList.add("d-none")
  }

  selectedScopeMode() {
    if (this.forcedProductScopeValue || this.hubVariantValue === "template") return "template"

    const checked = this.scopeModeTargets.find((input) => input.checked)
    return checked ? checked.value : "filtered"
  }

  canPreview() {
    if (this.selectedScopeMode() === "template" && !this.selectedTemplateId()) return false
    return true
  }

  async searchTemplates() {
    if (!this.hasTemplateQueryTarget || !this.hasTemplateResultsTarget) return

    const query = this.templateQueryTarget.value.trim()
    if (query.length > 0 && this.hasTemplateSelectionTarget) {
      this.templateSelectionTarget.classList.add("d-none")
    }

    if (query.length < 2) {
      this.renderTemplateSearchMessage(query.length > 0 ? this.templateSearchMinLengthValue : null)
      return
    }

    try {
      const params = new URLSearchParams({ q: query })
      if (this.hasIndexOptionsValue && this.indexOptionsValue && Object.keys(this.indexOptionsValue).length > 0) {
        params.set("index_options", JSON.stringify(this.indexOptionsValue))
      }

      const response = await fetch(`${this.templatesSearchUrlValue}?${params}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.renderTemplateSearchMessage(payload.error || this.templateSearchErrorValue, "danger")
        return
      }

      const payload = await response.json()
      this.renderTemplateResults(payload.results || [])
    } catch (_error) {
      this.renderTemplateSearchMessage(this.templateSearchErrorValue, "danger")
    }
  }

  renderTemplateSearchMessage(message, tone = "muted") {
    this.templateResultsTarget.innerHTML = ""
    if (!message) {
      this.templateResultsTarget.classList.add("d-none")
      return
    }

    const item = document.createElement("div")
    item.className = `list-group-item small text-${tone}`
    item.setAttribute("role", "status")
    item.textContent = message
    this.templateResultsTarget.appendChild(item)
    this.templateResultsTarget.classList.remove("d-none")
  }

  renderTemplateResults(results) {
    this.templateResultsTarget.innerHTML = ""
    if (!results.length) {
      this.renderTemplateSearchMessage(this.templateSearchEmptyValue, "muted")
      return
    }

    results.forEach((template) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "list-group-item list-group-item-action small"
      const productLabel = template.sub_product_label || template.sub_product_slug
      button.textContent = `${template.title} (#${template.id} · ${productLabel})`
      button.addEventListener("click", () => this.selectTemplate(template))
      this.templateResultsTarget.appendChild(button)
    })
    this.templateResultsTarget.classList.remove("d-none")
  }

  selectTemplate(template) {
    this.applyPresetTemplate(template)
    if (this.hasTemplateResultsTarget) {
      this.templateResultsTarget.classList.add("d-none")
    }
    this.syncXlsxButtonVisibility()
    this.preview()
  }

  async preview() {
    if (!this.canPreview()) return

    const requestId = ++this.previewRequestId
    this.showPreviewLoading()

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: this.jsonHeaders(),
        body: JSON.stringify(this.buildPayload())
      })

      let payload = {}
      try {
        payload = await response.json()
      } catch (_) {
        throw new Error(this.previewFailedValue)
      }

      if (!response.ok) throw new Error(payload.error || this.previewFailedValue)
      if (requestId !== this.previewRequestId) return

      this.previewData = payload
      this.renderPreview(payload)
      this.renderIndexFilters(payload.index_filters_html)
      this.updateRegistryExports(payload.registry_exports || [])
      if (!this.registryExportKey()) {
        this.renderFieldGroups(payload.field_groups || [])
      }
      this.renderRecentExports(payload.recent_exports || [])
      this.resumePendingStream(payload.pending_stream)
      this.syncXlsxButtonVisibility(payload.trust_level)
      this.scrollPreviewIntoView()
    } catch (error) {
      if (requestId !== this.previewRequestId) return

      this.renderPreviewError(error.message)
      this.setStatus(error.message, "danger")
    }
  }

  showPreviewLoading() {
    this.previewBodyTarget.innerHTML = `
      <div class="d-flex align-items-center gap-2 text-muted">
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <span>${this.loadingValue}</span>
      </div>
    `
  }

  renderPreview(payload) {
    const registryExport = this.selectedRegistryExport(payload)
    const alerts = registryExport ? [] : (payload.warnings || [])
    const exceedsLimit = registryExport ? registryExport.exceeds_limit : payload.exceeds_limit
    const maxRows = payload.max_rows
    const scope = registryExport ? registryExport.explanation : payload.explanation

    let countLabel = null
    if (registryExport) {
      if (registryExport.estimated_rows != null) {
        countLabel = this.formatRowCount(registryExport.estimated_rows, this.rowCountLinesValue)
      }
    } else if (payload.estimated_rows != null) {
      countLabel = payload.row_count_label || this.formatRowCount(payload.estimated_rows, this.rowCountContractsValue)
    }

    const notes = [...alerts]
    if (exceedsLimit) notes.push(`Limite dépassée (max ${maxRows})`)

    this.previewBodyTarget.innerHTML = `
      <div class="exports-hub-preview">
        <span class="exports-hub-preview__icon" aria-hidden="true"><i class="fa-solid fa-file-export"></i></span>
        <div class="exports-hub-preview__main">
          ${countLabel ? this.previewCountHtml(countLabel, exceedsLimit) : ""}
          ${scope ? `<div class="exports-hub-preview__scope">${this.escapeHtml(scope)}</div>` : ""}
        </div>
      </div>
      ${notes.length ? `
        <ul class="exports-hub-preview__notes">
          ${notes.map((note) => `
            <li><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i><span>${this.escapeHtml(note)}</span></li>
          `).join("")}
        </ul>` : ""}
    `
  }

  // « 586 contrats » → grand chiffre + libellé discret.
  previewCountHtml(label, exceedsLimit) {
    const match = String(label).match(/^(\d[\d\s\u00a0\u202f.,]*?)\s*(\D.*)?$/)
    const number = match ? match[1].trim() : String(label)
    const noun = match?.[2]?.trim() || ""
    const tone = exceedsLimit ? " is-over-limit" : ""

    return `
      <div class="exports-hub-preview__count${tone}">
        <strong>${this.escapeHtml(number)}</strong>
        ${noun ? `<span>${this.escapeHtml(noun)}</span>` : ""}
      </div>
    `
  }

  selectedRegistryExport(payload) {
    const exportKey = this.registryExportKey()
    if (!exportKey) return null

    return (payload.registry_exports || []).find((item) => item.key === exportKey) || null
  }

  formatRowCount(count, template) {
    return (template || "%{count}").replace("%{count}", count)
  }

  renderPreviewError(message) {
    this.previewBodyTarget.innerHTML = `<p class="text-danger small mb-0">${message}</p>`
  }

  resetPreviewPlaceholder() {
    this.previewData = null
    this.previewBodyTarget.innerHTML = `<p class="text-muted small mb-0">${this.previewPlaceholderValue}</p>`
    this.syncXlsxButtonVisibility()
  }

  shouldShowXlsxExport(_trustLevel = null) {
    return true
  }

  syncXlsxButtonVisibility(trustLevel = null) {
    if (!this.hasRunXlsxButtonTarget) return

    const show = this.shouldShowXlsxExport(trustLevel)
    this.runXlsxButtonTarget.classList.toggle("d-none", !show)
  }

  scrollPreviewIntoView() {
    this.previewSectionTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  renderFieldsPlaceholder(message) {
    this.fieldGroupsSignature = null
    this.fieldGroupsTarget.innerHTML = `<p class="text-muted small mb-0">${message}</p>`
  }

  renderRecentExports(exports) {
    if (!this.hasRecentExportsTarget) return

    if (!exports.length) {
      this.recentExportsTarget.innerHTML = ""
      return
    }

    const title = "Exports récents"
    const items = exports.map((exportItem) => `
      <li class="mb-1">
        <a href="${this.escapeHtml(exportItem.url)}" target="_blank" class="text-decoration-none" download>
          <i class="${this.formatIconClass(exportItem.filename)} me-1" aria-hidden="true"></i>
          ${this.escapeHtml(exportItem.filename)} — ${this.escapeHtml(exportItem.created_at)}
        </a>
      </li>
    `).join("")

    this.recentExportsTarget.innerHTML = `
      <div class="mb-2">
        <h6 class="small text-muted mb-2">${title}</h6>
        <ul class="list-unstyled small mb-0">${items}</ul>
      </div>
    `
  }

  // Icône du format d'après l'extension, comme Exports::HubHelper::FORMAT_ICONS côté serveur.
  formatIconClass(filename) {
    const extension = String(filename || "").split(".").pop().toLowerCase()
    const icons = { csv: "fa-solid fa-file-csv", xlsx: "fa-solid fa-file-excel" }
    return icons[extension] || "fa-solid fa-file-arrow-down"
  }

  resumePendingStream(pendingStream) {
    const streamId = pendingStream?.stream_id ||
      this.statusTarget?.querySelector("[data-stream-id]")?.dataset.streamId
    if (streamId) this.startStreamPolling(streamId)
  }

  renderFieldGroups(groups) {
    if (!groups.length) {
      this.renderFieldsPlaceholder(this.fieldsHintValue)
      return
    }

    // Mêmes champs qu'au rendu précédent (retrait d'un filtre…) : on ne touche à rien, ni aux
    // cases cochées ni à l'animation d'arrivée des groupes.
    const signature = JSON.stringify(groups)
    if (signature === this.fieldGroupsSignature) return
    this.fieldGroupsSignature = signature

    // Champs qui changent (autre produit…) : ceux qui restent gardent leur case décochée.
    const unchecked = new Set(this.fieldCheckboxElements().filter((checkbox) => !checkbox.checked).map((checkbox) => checkbox.value))
    this.fieldGroupsTarget.innerHTML = groups.map((group) => this.renderFieldGroupSection(group)).join("")
    this.fieldCheckboxElements().forEach((checkbox) => {
      if (unchecked.has(checkbox.value)) checkbox.checked = false
    })
  }

  renderFieldGroupSection(group, depth = 0) {
    const groupKey = String(group.key || group.label)
    const indentClass = depth > 0 ? "ms-3 ps-3 border-start" : ""
    const headingClass = depth > 0 ? "small fw-semibold text-body-secondary mb-0" : "small fw-semibold text-body-secondary mb-0"
    const fields = (group.fields || []).map((field) => `
      <div class="col-md-6">
        <div class="form-check mb-1">
          <input class="form-check-input" type="checkbox" value="${field.key}" id="export_field_${field.key}" data-exports-hub-target="fieldCheckbox" data-field-group="${groupKey}" checked>
          <label class="form-check-label small" for="export_field_${field.key}">${field.label}</label>
        </div>
      </div>
    `).join("")

    const children = (group.children || []).map((child) => this.renderFieldGroupSection(child, depth + 1)).join("")
    const fieldsBlock = fields ? `<div class="row g-1 mb-2">${fields}</div>` : ""
    const hasSelectableFields = fields.length > 0

    return `
      <section class="mb-3 pb-2 ${depth === 0 ? "border-bottom" : ""} ${indentClass}" data-field-group="${groupKey}">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <h6 class="${headingClass}">${this.escapeHtml(group.label)}</h6>
          ${hasSelectableFields ? `
            <div class="d-flex gap-2">
              <button type="button"
                      class="btn btn-link bg-shadow btn-sm py-0"
                      data-action="exports-hub#selectGroupFields"
                      data-group-key="${groupKey}"
                      data-select="true">
                ${this.selectAllFieldsValue}
              </button>
              <button type="button"
                      class="btn btn-link bg-shadow btn-sm py-0"
                      data-action="exports-hub#selectGroupFields"
                      data-group-key="${groupKey}"
                      data-select="false">
                ${this.deselectAllFieldsValue}
              </button>
            </div>
          ` : ""}
        </div>
        ${fieldsBlock}
        ${children}
      </section>
    `
  }

  selectGroupFields(event) {
    const groupKey = event.currentTarget.dataset.groupKey
    const checked = event.currentTarget.dataset.select === "true"
    this.fieldCheckboxElements()
      .filter((checkbox) => checkbox.dataset.fieldGroup === groupKey)
      .forEach((checkbox) => { checkbox.checked = checked })
  }

  selectAllFields() {
    this.fieldCheckboxElements().forEach((checkbox) => { checkbox.checked = true })
  }

  deselectAllFields() {
    this.fieldCheckboxElements().forEach((checkbox) => { checkbox.checked = false })
  }

  fieldCheckboxElements() {
    return Array.from(this.element.querySelectorAll('[data-exports-hub-target="fieldCheckbox"]'))
  }

  async run(event) {
    const button = event.currentTarget
    const format = button.dataset.format
    const registryKey = this.registryExportKey()
    const registryRun = Boolean(registryKey && this.selectedTemplateId())

    const fieldKeys = this.selectedFieldKeys()
    if (!registryRun && !fieldKeys.length) {
      this.setStatus("Sélectionnez au moins un champ.", "warning")
      return
    }

    this.setRunBusy(button, true)
    try {
      if (registryRun) {
        await this.runRegistryExport(registryKey, format)
      } else {
        await this.submitExport({ ...this.buildPayload(), export_format: format, field_keys: fieldKeys })
      }
    } finally {
      this.setRunBusy(button, false)
    }
  }

  // Le temps que l'export soit lancé : spinner sur le bouton cliqué, les deux boutons inactifs.
  setRunBusy(button, busy) {
    const buttons = [
      this.hasRunCsvButtonTarget ? this.runCsvButtonTarget : null,
      this.hasRunXlsxButtonTarget ? this.runXlsxButtonTarget : null
    ].filter(Boolean)
    buttons.forEach((element) => { element.disabled = busy })
    button.classList.toggle("is-busy", busy)
    button.setAttribute("aria-busy", String(busy))
  }

  async runRegistryExport(exportKey, format) {
    const formData = new FormData()
    formData.append("template_id", this.selectedTemplateId())
    formData.append("export_key", exportKey)
    formData.append("export_format", format)

    const response = await fetch(this.runUrlValue, {
      method: "POST",
      headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": this.csrfToken() },
      body: formData
    })

    await this.handleRunResponse(response, "Export métier lancé.", "Échec de l'export métier")
  }

  async submitExport(payload) {
    const response = await fetch(this.runUrlValue, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify(payload)
    })

    await this.handleRunResponse(response, "Export lancé en arrière-plan.", "Échec de l'export")
  }

  async handleRunResponse(response, successMessage, fallbackError) {
    const text = await response.text()
    const contentType = response.headers.get("content-type") || ""

    if (response.ok) {
      Turbo.renderStreamMessage(text)
      const streamId = this.extractStreamId(text)
      if (streamId) this.startStreamPolling(streamId)
      return
    }

    if (contentType.includes("turbo-stream")) {
      Turbo.renderStreamMessage(text)
      return
    }

    try {
      const payload = JSON.parse(text)
      this.setStatus(payload.error || fallbackError, "danger")
    } catch (_) {
      this.setStatus(fallbackError, "danger")
    }
  }

  selectedFieldKeys() {
    return this.fieldCheckboxElements()
      .filter((element) => element.checked)
      .map((element) => element.value)
  }

  buildPayload() {
    const payload = { scope_mode: this.selectedScopeMode() }

    payload.index_options = this.indexOptionsValue || {}
    payload.index_query = this.indexQueryValue || ""

    if (payload.scope_mode !== "filtered") {
      payload.template_id = this.selectedTemplateId()
    }

    if (this.registryExportSelected()) {
      payload.export_key = this.registryExportTarget.value
    }

    return payload
  }

  extractStreamId(text) {
    const dataMatch = text.match(/data-stream-id="([^"]+)"/)
    if (dataMatch?.[1]) return dataMatch[1]

    const progressMatch = text.match(/exports_hub_progress_([a-zA-Z0-9_]+)/)
    return progressMatch?.[1] || null
  }

  startStreamPolling(streamId) {
    if (!this.hasStreamStatusUrlValue) return

    this.activeStreamId = streamId
    this.streamFinished = false
    this.stopStreamPolling()
    this.streamPollInterval = window.setInterval(() => this.pollStreamStatus(streamId), 1500)
    this.pollStreamStatus(streamId)
  }

  stopStreamPolling() {
    if (this.streamPollInterval) {
      window.clearInterval(this.streamPollInterval)
      this.streamPollInterval = null
    }
  }

  async pollStreamStatus(streamId) {
    try {
      const url = `${this.streamStatusUrlValue}?stream_id=${encodeURIComponent(streamId)}`
      const response = await fetch(url, {
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken() }
      })

      if (response.status === 204) return

      const payload = await response.json()

      if (payload.status === "preparing") {
        this.renderPreparing(streamId, payload.message)
        return
      }

      if (payload.status === "progress") {
        this.renderProgress(streamId, payload.current, payload.total)
        return
      }

      if (payload.status === "completed" || payload.status === "error") {
        if (this.streamFinished) return

        this.streamFinished = true
        this.stopStreamPolling()

        const resultsElement = document.getElementById(`exports_hub_results_${streamId}`)
        if (!resultsElement?.children.length) {
          await this.renderStreamStatusFallback(streamId)
        }
      }
    } catch (_) {
      // Polling de secours : on ignore les erreurs réseau ponctuelles.
    }
  }

  async renderStreamStatusFallback(streamId) {
    try {
      const url = `${this.streamStatusUrlValue}?stream_id=${encodeURIComponent(streamId)}`
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html", "X-CSRF-Token": this.csrfToken() }
      })

      if (response.status === 204) return

      const text = await response.text()
      if (text) Turbo.renderStreamMessage(text)
    } catch (_) {
      // Ignore
    }
  }

  renderPreparing(streamId, message) {
    const messageText = message || this.preparingDescriptionValue || ""
    const streamContainer = this.statusTarget?.querySelector(`[data-stream-id="${streamId}"]`)
    const titleElement = streamContainer?.querySelector(".depozen-ui-callout__title")
    const descriptionElement = streamContainer?.querySelector(".depozen-ui-callout__description")
    if (titleElement) {
      titleElement.textContent = this.preparingTitleValue || "Export volumineux"
    }
    if (descriptionElement) {
      descriptionElement.textContent = messageText
    }

    const element = document.getElementById(`exports_hub_progress_${streamId}`)
    if (!element) return

    element.innerHTML = `
      <div class="progress" style="height: 0.5rem;" role="progressbar" aria-label="Préparation de l'export">
        <div class="progress-bar progress-bar-striped progress-bar-animated" style="width: 100%;"></div>
      </div>
      <p class="small text-muted mb-0 mt-1">${this.escapeHtml(messageText)}</p>
    `
  }

  renderProgress(streamId, current, total) {
    const element = document.getElementById(`exports_hub_progress_${streamId}`)
    if (!element) return

    const percent = total > 0 ? Math.min(Math.round((Number(current) / Number(total)) * 100), 100) : 0
    const unitLabel = Number(total) === 1 ? "ligne" : "lignes"
    if (this.updateProgressInPlace(element, `${percent}%`, percent, `${current} / ${total} ${unitLabel}`)) return

    element.innerHTML = `
      <div class="progress${percent === 0 ? " is-loading" : ""}" style="height: 0.5rem;" role="progressbar" aria-valuenow="${percent}" aria-valuemin="0" aria-valuemax="100" aria-label="Progression">
        <div class="progress-bar progress-bar-animated" style="width: ${percent}%;"></div>
      </div>
      <p class="small text-muted mb-0 mt-1">${current} / ${total} ${unitLabel}</p>
    `
  }

  // Barre déjà affichée (hors « préparation », rayée) : on lui donne la nouvelle largeur
  // plutôt que de la remplacer, pour qu'elle glisse (cf. _exports_hub.scss).
  updateProgressInPlace(element, width, percent, text) {
    const bar = element?.querySelector(".progress-bar:not(.progress-bar-striped)")
    if (!bar) return false

    bar.style.width = width
    bar.parentElement.setAttribute("aria-valuenow", percent)
    // Piste en squelette tant que rien n'est traité (même matière que les chargements de l'app).
    bar.parentElement.classList.toggle("is-loading", Number(percent) === 0)
    const label = element.querySelector("p")
    if (label) label.textContent = text
    return true
  }

  // Les diffusions du job remplacent la barre à chaque progression : on reporte leur largeur
  // sur la barre affichée au lieu de la remplacer.
  smoothProgressStream(event) {
    const stream = event.target
    const targetId = stream.getAttribute?.("target") || ""
    if (stream.getAttribute?.("action") !== "replace" || !targetId.startsWith("exports_hub_progress_")) return

    const current = document.getElementById(targetId)
    if (!current || !this.element.contains(current)) return

    const next = stream.templateContent?.firstElementChild
    const nextBar = next?.querySelector(".progress-bar:not(.progress-bar-striped)")
    if (!nextBar || !current.querySelector(".progress-bar:not(.progress-bar-striped)")) return

    const percent = nextBar.parentElement.getAttribute("aria-valuenow")
    const text = next.querySelector("p")?.textContent?.trim() || ""
    event.detail.render = () => this.updateProgressInPlace(current, nextBar.style.width, percent, text)
  }

  renderCompleted(payload) {
    const streamId = this.activeStreamId
    const resultsElement = streamId ? document.getElementById(`exports_hub_results_${streamId}`) : null
    const target = resultsElement || (this.hasStatusTarget ? this.statusTarget : null)
    if (!target) return

    const extension = payload.extension || "csv"
    const title = this.escapeHtml(payload.title || this.completedTitleValue || "Export terminé")
    const description = this.escapeHtml(payload.description || "")
    const downloadUrl = payload.download_url || ""
    const downloadLabel = this.escapeHtml(
      (this.downloadFileValue || "Télécharger le fichier .%{extension}").replace("%{extension}", extension)
    )

    target.innerHTML = `
      <div class="depozen-ui-callout depozen-ui-callout--success mb-2 p-2 small">
        <strong>${title}</strong>
        <div class="mb-0">${description}</div>
      </div>
      ${downloadUrl ? `
        <a href="${this.escapeHtml(downloadUrl)}" class="btn btn-success" target="_blank" download>
          <i class="fas fa-download me-2"></i>
          ${downloadLabel}
        </a>
      ` : ""}
    `
  }

  renderError(payload) {
    const streamId = this.activeStreamId
    const resultsElement = streamId ? document.getElementById(`exports_hub_results_${streamId}`) : null
    const target = resultsElement || (this.hasStatusTarget ? this.statusTarget : null)
    if (!target) return

    const title = this.escapeHtml(payload.title || this.errorTitleValue || "Export impossible")
    const message = this.escapeHtml(payload.message || "")
    target.innerHTML = `
      <div class="depozen-ui-callout depozen-ui-callout--danger mb-0 p-2 small">
        <strong>${title}</strong>
        <div class="mb-0">${message}</div>
      </div>
    `
  }

  jsonHeaders() {
    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  setStatus(message, variant = "info") {
    if (!message) {
      this.statusTarget.innerHTML = ""
      return
    }
    this.statusTarget.innerHTML = `<div class="depozen-ui-callout depozen-ui-callout--${variant} mb-0 p-2 small">${message}</div>`
  }
}
